import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import store from '../store';
import { isEmailVerificationRequired, isTestEmail, isPasswordChangeRequired } from '@/utils/authUtils';
import { getPartnerCheckoutQuery, isPartnerCheckoutReturnRoute } from '@/utils/partnerCheckoutReturn';
import { waitForFirebaseAuth } from '@/utils/firebaseUtils';
import { ensureLoginStatusResolved } from '@/utils/ensureLoginStatus';
import { checkOwnerPinRoute } from '@/utils/requireOwnerPin';
import { kitchenRouteGuard } from '@/utils/kitchenRouteGuard';

/**
 * The 409 login response includes the company (with passwordChangeRequired), but a hard
 * reload straight into a /navigation/... URL can reach the guard before that data has been
 * loaded into the store. Fetch it on demand so the forced-password-change check is reliable.
 */
const ensureCompanyLoaded = async () => {
  if (store.state.profile.companyData) {
    return store.state.profile.companyData;
  }
  await store.dispatch('profile/requestCompany');
  return store.state.profile.companyData;
};

const shouldSkipVerifyEmailRedirect = (to) => (
  store.state.registrationInProgress || to.name === 'login.profile'
);

const isStripePartnerReturn = (to) => {
  if (to?.name === 'navigation.checkout-return') return true;
  if (!getPartnerCheckoutQuery({ currentRoute: { value: to } }).checkout) return false;
  return isPartnerCheckoutReturnRoute(to);
};

/**
 * While the logout action runs, the Firebase token is still briefly valid. Guards must not
 * re-resolve the session in that window, otherwise the user is pulled back into the app.
 */
const isLogoutInProgress = () => store.state.logoutInProgress === true;

const checkAuthentication = async (to, from, next) => {
  if (isLogoutInProgress()) {
    next({ name: 'login' });
    return;
  }

  const stripeReturn = isStripePartnerReturn(to);
  let user = firebase.auth().currentUser;
  if (!user && stripeReturn) {
    user = await waitForFirebaseAuth();
  }
  if (user && isEmailVerificationRequired(user) && !shouldSkipVerifyEmailRedirect(to)) {
    next({ name: 'login.verify-email' });
    return;
  }

  const { loginStatusCode } = user && !isEmailVerificationRequired(user)
    ? await ensureLoginStatusResolved()
    : { loginStatusCode: store.state.loginStatusCode };

  if (loginStatusCode === 204) {
    next({ name: 'login.profile' });
  } else if (loginStatusCode === 409) {
    const company = await ensureCompanyLoaded();
    if (isPasswordChangeRequired(company)) {
      next({ name: 'login.change-password' });
      return;
    }
    next();
  } else if (isStripePartnerReturn(to) && user) {
    next();
  } else {
    next({ name: 'login' });
  }
};

const checkPasswordChangeRequired = async (to, from, next) => {
  if (isLogoutInProgress()) {
    next({ name: 'login' });
    return;
  }

  let user = firebase.auth().currentUser;
  if (!user) {
    user = await waitForFirebaseAuth();
  }
  if (!user) {
    next({ name: 'login' });
    return;
  }
  if (isEmailVerificationRequired(user)) {
    next({ name: 'login.verify-email' });
    return;
  }

  const { loginStatusCode } = await ensureLoginStatusResolved();
  if (loginStatusCode === 204) {
    next({ name: 'login.profile' });
    return;
  }
  if (loginStatusCode !== 409) {
    next({ name: 'login' });
    return;
  }

  const company = await ensureCompanyLoaded();
  if (!isPasswordChangeRequired(company)) {
    next({ name: 'navigation' });
    return;
  }
  next();
};

const checkLoggedIn = async (to, from, next) => {
  if (isLogoutInProgress()) {
    if (to.name === 'login') {
      next();
      return;
    }
    next({ name: 'login' });
    return;
  }

  let user = firebase.auth().currentUser;
  if (!user) {
    user = await waitForFirebaseAuth();
  }
  if (user && isEmailVerificationRequired(user) && !shouldSkipVerifyEmailRedirect(to)) {
    next({ name: 'login.verify-email' });
    return;
  }

  const { loginStatusCode } = user && !isEmailVerificationRequired(user)
    ? await ensureLoginStatusResolved()
    : { loginStatusCode: store.state.loginStatusCode };

  if (loginStatusCode === 204) {
    next({ name: 'login.profile' });
  } else if (loginStatusCode === 409) {
    next({ name: 'navigation' });
  } else {
    next();
  }
};

const checkProfile = async (to, from, next) => {
  let user = firebase.auth().currentUser;
  if (!user) {
    user = await waitForFirebaseAuth();
  }
  if (!user) {
    next({ name: 'login' });
    return;
  }

  if (!isEmailVerificationRequired(user)) {
    const { loginStatusCode } = await ensureLoginStatusResolved();
    if (loginStatusCode === 409) {
      next({ name: 'navigation' });
      return;
    }
    if (loginStatusCode === 204) {
      next();
      return;
    }
    next({ name: 'login' });
    return;
  }

  if (isEmailVerificationRequired(user)) {
    next();
    return;
  }

  next({ name: 'login' });
};

const checkVerifyEmail = async (to, from, next) => {
  const user = firebase.auth().currentUser;
  if (!user) {
    next({ name: 'login' });
    return;
  }

  if (user.emailVerified || isTestEmail(user.email)) {
    next({ name: 'login' });
    return;
  }

  next();
};

export default [
  {
    path: '/kueche/einrichten',
    name: 'kitchen.setup',
    component: () => import('@/views/Kitchen/KitchenSetup.vue'),
    beforeEnter: kitchenRouteGuard,
  },
  {
    path: '/kueche/push',
    name: 'kitchen.push-prompt',
    component: () => import('@/views/Kitchen/KitchenPushPrompt.vue'),
    beforeEnter: kitchenRouteGuard,
  },
  {
    path: '/kueche/pin',
    name: 'kitchen.pin',
    component: () => import('@/views/Kitchen/KitchenPin.vue'),
    beforeEnter: kitchenRouteGuard,
  },
  {
    path: '/kueche',
    name: 'kitchen.queue',
    component: () => import('@/views/Preparation/PreparationQueue.vue'),
    props: { kitchenMode: true },
    beforeEnter: kitchenRouteGuard,
  },
  {
    path: '/kueche/einstellungen',
    name: 'kitchen.settings',
    component: () => import('@/views/Preparation/PreparationSettings.vue'),
    props: { kitchenMode: true },
    beforeEnter: kitchenRouteGuard,
  },
  {
    path: '/kueche/ungueltig',
    name: 'kitchen-invalid',
    component: () => import('@/views/Kitchen/KitchenInvalid.vue'),
  },
  {
    path: '/pin-setup',
    name: 'pin-setup',
    component: () => import('@/views/PinSetup.vue'),
  },
  {
    path: '/pin-disable',
    name: 'pin-disable',
    component: () => import('@/views/PinDisable.vue'),
  },
  {
    path: '/owner-pin-setup',
    name: 'owner-pin-setup',
    component: () => import('@/views/OwnerPinSetup.vue'),
  },
  {
    path: '/login',
    component: () => import('@/views/Login.vue'),
    children: [
      {
        path: '',
        redirect: '/login/main',
      },
      {
        path: 'main',
        name: 'login',
        component: () => import('@/views/LoginMain.vue'),
        beforeEnter: checkLoggedIn,
      },
      {
        path: 'register',
        redirect: '/login/main',
      },
      {
        path: 'reset',
        name: 'login.reset',
        component: () => import('@/views/LoginReset.vue'),
        beforeEnter: checkLoggedIn,
        meta: {
          protected: true,
        },
      },
      {
        path: 'email-action',
        name: 'login.email-action',
        component: () => import('@/views/LoginEmailAction.vue'),
      },
      {
        path: 'verify-email',
        name: 'login.verify-email',
        component: () => import('@/views/LoginVerifyEmail.vue'),
        beforeEnter: checkVerifyEmail,
        meta: {
          protected: true,
        },
      },
      {
        path: 'profile',
        name: 'login.profile',
        component: () => import('@/views/LoginProfile.vue'),
        beforeEnter: checkProfile,
        meta: {
          protected: true,
        },
      },
      {
        path: 'change-password',
        name: 'login.change-password',
        component: () => import('@/views/LoginChangePassword.vue'),
        beforeEnter: checkPasswordChangeRequired,
        meta: {
          protected: true,
        },
      },
    ],
  },
  {
    path: '/navigation',
    component: () => import('@/views/Navigation.vue'),
    beforeEnter: checkAuthentication,
    children: [
      {
        path: '',
        name: 'navigation',
        redirect: '/navigation/profil',
      },
      {
        path: 'cockpit',
        name: 'navigation.cockpit',
        component: () => import('@/views/Cockpit/Cockpit.vue'),
        meta: { protected: true, requiresOwnerPin: true },
        beforeEnter: checkOwnerPinRoute,
      },
      {
        path: 'specials',
        name: 'navigation.specials',
        component: () => import('@/views/Specials/Specials.vue'),
        meta: { protected: true, requiresOwnerPin: true },
        beforeEnter: checkOwnerPinRoute,
      },
      {
        path: 'games',
        name: 'navigation.games',
        component: () => import('@/views/Games/Games.vue'),
        meta: { protected: true },
      },
      {
        path: 'profil',
        name: 'navigation.profile',
        component: () => import('@/views/Profil/Profil.vue'),
        meta: { protected: true },
      },
      {
        path: 'zubereitung',
        name: 'navigation.preparation',
        component: () => import('@/views/Preparation/PreparationQueue.vue'),
        meta: { protected: true },
      },
      {
        path: 'zubereitung/einstellungen',
        name: 'navigation.preparation-settings',
        component: () => import('@/views/Preparation/PreparationSettings.vue'),
        meta: { protected: true },
      },
      {
        path: 'praesentation',
        name: 'navigation.presentation',
        component: () => import('@/views/Profile/CompanyPresentation.vue'),
        meta: { protected: true },
      },
      {
        path: 'profile',
        redirect: { name: 'navigation.presentation' },
      },
      {
        path: 'checkout-return',
        name: 'navigation.checkout-return',
        component: () => import('@/views/CheckoutReturn.vue'),
        meta: { protected: true },
      },
      {
        path: 'membership',
        name: 'navigation.membership',
        redirect: { name: 'navigation.profile' },
      },
      {
        path: 'partner',
        name: 'navigation.partner',
        component: () => import('@/views/Partner/PartnerMonetization.vue'),
        meta: { protected: true, requiresOwnerPin: true },
        beforeEnter: checkOwnerPinRoute,
      },
      {
        path: 'highlights',
        name: 'navigation.highlights',
        component: () => import('@/views/Partner/PartnerMonetization.vue'),
        meta: { protected: true, requiresOwnerPin: true },
        beforeEnter: checkOwnerPinRoute,
      },
      {
        path: 'werbebudget',
        name: 'navigation.werbebudget',
        component: () => import('@/views/Partner/Werbebudget.vue'),
        meta: { protected: true, requiresOwnerPin: true },
        beforeEnter: checkOwnerPinRoute,
      },
      {
        path: 'provision',
        name: 'navigation.provision',
        component: () => import('@/views/Partner/Provision.vue'),
        meta: { protected: true },
      },
      {
        path: 'provision/invoice/:invoiceId',
        name: 'navigation.provision-invoice',
        component: () => import('@/views/Partner/ProvisionDetail.vue'),
        meta: { protected: true },
        props: true,
      },
      {
        path: 'provision/:periodMonth',
        name: 'navigation.provision-month',
        component: () => import('@/views/Partner/ProvisionDetail.vue'),
        meta: { protected: true },
        props: true,
      },
      {
        path: 'payment',
        name: 'navigation.payment',
        redirect: { name: 'navigation.membership' },
      },
      {
        path: 'payment/buy/:planID',
        name: 'navigation.payment.buy',
        redirect: { name: 'navigation.membership' },
      },
      {
        path: 'payment/cancel/:planID',
        name: 'navigation.payment.cancel',
        redirect: { name: 'navigation.membership' },
      },
      {
        path: 'payment/update/:subscriptionID',
        name: 'navigation.payment.update',
        redirect: { name: 'navigation.membership' },
      },
      {
        path: 'bonus',
        name: 'navigation.bonus',
        component: () => import('@/views/Bonus/Bonus.vue'),
        meta: { protected: true },
      },
      {
        path: 'bewertung',
        name: 'navigation.bewertung',
        component: () => import('@/views/Bewertung/Bewertung.vue'),
        meta: { protected: true },
      },
      {
        path: 'support',
        redirect: { name: 'navigation.profile' },
      },
      {
        path: 'support/invite',
        beforeEnter() {
          store.commit('ui/openInviteGuests');
        },
        redirect: { name: 'navigation.profile' },
      },
      {
        path: 'profil/invite',
        beforeEnter() {
          store.commit('ui/openInviteGuests');
        },
        redirect: { name: 'navigation.profile' },
      },
    ],
  },
];
